import {
  ActionBar,
  ActionInfo,
  Badge,
  Card,
  DataTable,
  ErrorState,
  Facts,
  Loading,
  PageHead,
  formatDate,
  formatMoney,
} from "@corecrm/ui";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useInvoice, useInvoices } from "../api/queries";
import type { Invoice, InvoiceSummary } from "../api/types";
import { Crumb, LoadError, Status } from "../components/bits";
import { PAYMENT_METHOD, quantity } from "../lib/labels";

/** Pay on Stripe's hosted page (a new tab), while the invoice is open and has one. */
function PayLink({ invoice: i, size }: { invoice: InvoiceSummary; size?: "sm" }) {
  if (!i.pay_url) return null;
  return (
    <a
      className={size ? "btn sm primary" : "btn primary"}
      href={i.pay_url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      Pay
    </a>
  );
}

function InvoiceStatus({ invoice: i }: { invoice: InvoiceSummary }) {
  return i.overdue ? <Badge kind="bad">Overdue</Badge> : <Status status={i.status} />;
}

/** The account's issued invoices, newest first. The prototype's pInvoices(). */
export function InvoicesPage() {
  const invoices = useInvoices();
  const navigate = useNavigate();
  if (invoices.isPending) return <Loading />;
  if (invoices.error) return <ErrorState error={invoices.error} onRetry={() => invoices.refetch()} />;
  return (
    <DataTable<InvoiceSummary>
      label="Invoices"
      rows={invoices.data}
      rowKey={(i) => i.id}
      onRowClick={(i) => navigate(`/invoices/${i.id}`)}
      empty="No invoices yet."
      columns={[
        { header: "Invoice", cell: (i) => <span className="mono">{i.number ?? "—"}</span> },
        { header: "Issued", cell: (i) => formatDate(i.issued_at) },
        { header: "Due", cell: (i) => formatDate(i.due_date) },
        { header: "Status", cell: (i) => <InvoiceStatus invoice={i} /> },
        {
          header: "Total",
          align: "right",
          cell: (i) => (
            <span className="mono">
              {formatMoney(i.total)}
              {i.status === "open" && i.balance.amount_minor !== i.total.amount_minor && (
                <span className="sub">{formatMoney(i.balance)} left</span>
              )}
            </span>
          ),
        },
        { header: "", role: "ctl", align: "right", cell: (i) => <PayLink invoice={i} size="sm" /> },
      ]}
    />
  );
}

export function InvoicePage() {
  const { id = "" } = useParams();
  const invoice = useInvoice(id);
  if (invoice.isPending) return <Loading />;
  if (invoice.error)
    return (
      <LoadError
        error={invoice.error}
        onRetry={() => invoice.refetch()}
        what="invoice"
        back={["/invoices", "Back to invoices"]}
      />
    );
  return <InvoiceView invoice={invoice.data} />;
}

/** An issued invoice; `preview` is staff's Preview as client (UX-113): no way back to the list. */
export function InvoiceView({ invoice: i, preview = false }: { invoice: Invoice; preview?: boolean }) {
  return (
    <>
      {!preview && (
        <Crumb to="/invoices" label="Invoices">
          {i.number ?? "Invoice"}
        </Crumb>
      )}
      <PageHead
        title={`Invoice ${i.number ?? ""}`}
        sub={
          <>
            <InvoiceStatus invoice={i} />
            <span>Issued {formatDate(i.issued_at)}</span>
            <span>Due {formatDate(i.due_date)}</span>
          </>
        }
        actions={
          <>
            {i.pdf_url && (
              <a className="btn bar-dup" href={i.pdf_url} target="_blank" rel="noopener noreferrer">
                PDF
              </a>
            )}
            {i.pay_url && (
              <a className="btn primary bar-dup" href={i.pay_url} target="_blank" rel="noopener noreferrer">
                Pay {formatMoney(i.balance)}
              </a>
            )}
          </>
        }
      />
      {i.project_id && (
        <p className="muted">
          For <Link to={`/projects/${i.project_id}`}>your project</Link>.
        </p>
      )}
      {i.memo && <p style={{ whiteSpace: "pre-line" }}>{i.memo}</p>}
      <Card title="Items">
        <DataTable
          label="Invoice items"
          rows={i.lines}
          rowKey={(l) => `${l.description}${l.amount.amount_minor}`}
          columns={[
            { header: "Item", cell: (l) => l.description },
            {
              header: "Qty",
              align: "right",
              cell: (l) => <span className="mono">{quantity(l.quantity)}</span>,
            },
            {
              header: "Unit",
              align: "right",
              cell: (l) => <span className="mono">{formatMoney(l.unit_amount)}</span>,
            },
            {
              header: "Amount",
              align: "right",
              cell: (l) => <span className="mono">{formatMoney(l.amount)}</span>,
            },
          ]}
        />
        <div className="totals" style={{ marginTop: 12, marginLeft: "auto", maxWidth: 320 }}>
          <div className="ln">
            <span>Subtotal</span>
            <span>{formatMoney(i.subtotal)}</span>
          </div>
          {i.tax_lines.length ? (
            i.tax_lines.map((t) => (
              <div className="ln" key={`${t.name} ${t.percentage}`}>
                <span>
                  {t.name} {Number(t.percentage)}%
                  {t.registration_number && <span className="faint"> · no. {t.registration_number}</span>}
                </span>
                <span>{formatMoney(t.amount)}</span>
              </div>
            ))
          ) : (
            <div className="ln">
              <span>Tax</span>
              <span>{formatMoney(i.tax)}</span>
            </div>
          )}
          <div className="ln grand">
            <span>Total</span>
            <span>{formatMoney(i.total)}</span>
          </div>
          {i.amount_paid.amount_minor > 0 && (
            <div className="ln">
              <span>Paid</span>
              <span>−{formatMoney(i.amount_paid)}</span>
            </div>
          )}
          <div className="ln grand">
            <span>Balance</span>
            <span>{formatMoney(i.balance)}</span>
          </div>
        </div>
      </Card>
      {i.payments.length > 0 && (
        <Card title="Payments">
          <Facts
            items={i.payments.map((p) => [
              formatDate(p.paid_at),
              <span>
                <span className="mono">{formatMoney(p.amount)}</span> · {PAYMENT_METHOD[p.method] ?? p.method}
              </span>,
            ])}
          />
        </Card>
      )}
      {(i.pay_url || i.pdf_url) && (
        <ActionBar
          info={i.pay_url ? <ActionInfo label="Balance" value={formatMoney(i.balance)} /> : undefined}
        >
          {i.pdf_url && (
            <a className="btn" href={i.pdf_url} target="_blank" rel="noopener noreferrer">
              PDF
            </a>
          )}
          <PayLink invoice={i} />
        </ActionBar>
      )}
    </>
  );
}
